import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import type { Language, TranslationKey } from './catalogue'
import { DEFAULT_LANGUAGE, isLanguage, resolveLanguage, translate } from './catalogue'

const STORAGE_KEY = 'expat-passport.language'

export type Translate = (key: TranslationKey, params?: Record<string, string | number>) => string

interface Localization {
  language: Language
  setLanguage: (language: Language) => void
  t: Translate
}

const LocalizationContext = createContext<Localization | undefined>(undefined)

function stored(): Language | undefined {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY) ?? undefined

    return isLanguage(saved) ? saved : undefined
  } catch {
    return undefined
  }
}

function remember(language: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language)
  } catch {
    // A private window with storage disabled is not a reason to fail a render.
  }
}

export function LocalizationProvider({
  hint,
  children,
}: {
  hint?: string | undefined
  children: ReactNode
}) {
  const [language, setLanguageState] = useState<Language>(() =>
    resolveLanguage(stored(), hint, navigator.language, DEFAULT_LANGUAGE),
  )

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next)
    remember(next)
  }, [])

  const value = useMemo<Localization>(
    () => ({
      language,
      setLanguage,
      t: (key, params) => translate(language, key, params),
    }),
    [language, setLanguage],
  )

  return <LocalizationContext value={value}>{children}</LocalizationContext>
}

export function useLocalization(): Localization {
  const value = useContext(LocalizationContext)

  if (!value) {
    throw new Error('useLocalization is only usable inside a LocalizationProvider')
  }

  return value
}

export function useTranslate(): Translate {
  return useLocalization().t
}
